import React, { useState, useCallback, useRef, useEffect, lazy, Suspense } from 'react';
import {
  Upload,
  Film,
  Library,
  Radio,
  Clapperboard,
  X,
  Loader2,
  CheckCircle,
  AlertCircle,
  Clock,
  Zap,
  Target,
  FileText,
  ChevronDown,
  Play,
  Trash2,
  Search,
  RefreshCw,
  Cpu,
  Calendar,
  MessageSquare,
  Activity,
} from 'lucide-react';
import { C } from '../design-system/primitives/constants';
import { CAROLINA_BLUE } from '../../config/uncConstants';
import { ANALYSIS_MODES } from '../../lib/analysisModes';
import { analyzeMedia } from '../../lib/geminiService';
import { MediaType } from '../../lib/types';
import { EXPERT_PERSONAS } from '../../lib/expertPersonas';
import ScoutingIntelOverlay from './ScoutingIntelOverlay';
import VisionModelCards from './VisionModelCards';
import PracticeFilmOverlay from './PracticeFilmOverlay';
import PracticeHeatmap from './PracticeHeatmap';

const LiveVideoTab = lazy(() => import('../video/LiveVideoTab'));

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
const MAX_FILE_SIZE_LEGACY = 100 * 1024 * 1024; // 100MB for base64 path
const MAX_FILE_SIZE_GCS = 10 * 1024 * 1024 * 1024; // 10GB for GCS signed URL path
const MAX_FILES = 10;

const SUB_TABS = [
  { id: 'upload', label: 'Upload & Analyze', icon: Upload },
  { id: 'practice', label: 'Practice Film', icon: Clapperboard },
  { id: 'game-film', label: 'Game Film', icon: Film },
  { id: 'library', label: 'Library', icon: Library },
  { id: 'live', label: 'Live', icon: Radio },
  { id: 'vision-tools', label: 'Vision Tools', icon: Cpu },
];

const ANALYSIS_TIERS = [
  { id: 'simple-mechanics', label: 'Quick Scan', icon: Zap },
  { id: 'pitch-tipping', label: 'Pitch Tipping', icon: Target },
  { id: 'deep-analysis-baseball', label: 'Deep Analysis', icon: FileText },
];

const PLAYER_CONTEXTS = ['Own Pitcher', 'Opponent'];

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function parseFilename(filename) {
  const baseName = filename.replace(/\.[^/.]+$/, '');
  const parts = baseName.split(/[_\-\s]+/);
  const pitchTypes = ['fastball', 'curveball', 'slider', 'changeup', 'cutter', 'sinker', 'splitter'];
  let pitchType = null;
  for (const part of parts) {
    if (pitchTypes.includes(part.toLowerCase())) {
      pitchType = part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      break;
    }
  }
  return { pitchType };
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// EMPTY STATE
// ═══════════════════════════════════════════════════════════════════════════

function EmptyState({ icon: Icon, title, description }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
      <div
        className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5"
        style={{ background: C.s2, border: `1px solid ${C.border}` }}
      >
        <Icon size={28} style={{ color: CAROLINA_BLUE }} />
      </div>
      <h3 className="text-base font-semibold mb-2" style={{ color: C.text }}>{title}</h3>
      <p className="text-sm max-w-md" style={{ color: C.t2 }}>{description}</p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FILE CARD
// ═══════════════════════════════════════════════════════════════════════════

function FileCard({ file, onRemove, progress, status }) {
  const { pitchType } = parseFilename(file.name);
  const ext = file.name.split('.').pop().toUpperCase();

  return (
    <div
      className="rounded-lg p-3 relative group"
      style={{ background: C.s2, border: `1px solid ${C.border}` }}
    >
      {/* Remove button */}
      {status !== 'analyzing' && (
        <button
          onClick={onRemove}
          className="absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ background: '#FF4444' }}
        >
          <X size={10} color="#fff" />
        </button>
      )}

      {/* Thumbnail placeholder */}
      <div
        className="w-full h-20 rounded mb-2 flex items-center justify-center"
        style={{ background: C.s1 }}
      >
        {status === 'complete' ? (
          <CheckCircle size={20} style={{ color: '#00CC66' }} />
        ) : status === 'analyzing' ? (
          <Loader2 size={20} className="animate-spin" style={{ color: CAROLINA_BLUE }} />
        ) : status === 'error' ? (
          <AlertCircle size={20} style={{ color: '#FF4444' }} />
        ) : (
          <Play size={20} style={{ color: C.t3 }} />
        )}
      </div>

      {/* Filename */}
      <p className="text-[11px] font-medium truncate mb-0.5" style={{ color: C.text }}>
        {file.name}
      </p>

      {/* Meta row */}
      <div className="flex items-center gap-2">
        <span className="text-[10px]" style={{ color: C.t3 }}>{formatBytes(file.size)}</span>
        <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: C.s3, color: C.t2 }}>
          {ext}
        </span>
        {pitchType && (
          <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: C.goldDim, color: CAROLINA_BLUE }}>
            {pitchType}
          </span>
        )}
      </div>

      {/* Progress bar */}
      {status === 'analyzing' && typeof progress === 'number' && (
        <div className="mt-2 h-1 rounded-full overflow-hidden" style={{ background: C.s1 }}>
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${progress}%`,
              background: `linear-gradient(90deg, ${C.goldDim}, ${CAROLINA_BLUE})`,
            }}
          />
        </div>
      )}

      {/* Status label */}
      {status === 'complete' && (
        <p className="text-[10px] mt-1.5 font-medium" style={{ color: '#00CC66' }}>Complete</p>
      )}
      {status === 'analyzing' && (
        <p className="text-[10px] mt-1.5 font-medium" style={{ color: CAROLINA_BLUE }}>
          {progress != null ? `${progress}%` : 'Analyzing...'}
        </p>
      )}
      {status === 'error' && (
        <p className="text-[10px] mt-1.5 font-medium" style={{ color: '#FF4444' }}>Failed</p>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// UPLOAD & ANALYZE TAB
// ═══════════════════════════════════════════════════════════════════════════

function UploadAnalyzeTab() {
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedTier, setSelectedTier] = useState('simple-mechanics');
  const [playerAssoc, setPlayerAssoc] = useState('unc-roster');
  const [selectedPlayer, setSelectedPlayer] = useState('');
  const [context, setContext] = useState('Own Pitcher');
  const [roster, setRoster] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [fileStatuses, setFileStatuses] = useState({});
  const [fileProgress, setFileProgress] = useState({});
  const [showPlayerDropdown, setShowPlayerDropdown] = useState(false);
  const fileInputRef = useRef(null);

  // Pipeline mode state (GCS signed URL + bbclip)
  const [analysisPrompt, setAnalysisPrompt] = useState('');
  const [opponentTeam, setOpponentTeam] = useState('');
  const [gameDate, setGameDate] = useState('');
  const [gameHalf, setGameHalf] = useState('first');
  const [pipelineSession, setPipelineSession] = useState(null); // active session
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [fileErrors, setFileErrors] = useState([]);

  // Pitch tipping overlay state
  const [overlayPhase, setOverlayPhase] = useState(null);
  const [overlayProgress, setOverlayProgress] = useState(0);
  const [overlayExpert, setOverlayExpert] = useState(null);
  const [overlayVideoUrl, setOverlayVideoUrl] = useState(null);
  const [overlayAnalysis, setOverlayAnalysis] = useState(null);
  const [overlayMeta, setOverlayMeta] = useState({});

  // Fetch roster
  useEffect(() => {
    fetch('/api/unc/roster')
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setRoster(Array.isArray(data) ? data : data.roster || []))
      .catch(() => setRoster([]));
  }, []);

  // Pipeline status polling
  useEffect(() => {
    if (!pipelineSession || ['complete', 'error', 'skipped'].includes(pipelineStatus?.status)) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`/api/unc/video/status/${pipelineSession}`);
        if (res.ok) {
          const data = await res.json();
          setPipelineStatus(data.data || data);
        }
      } catch { /* polling is best-effort */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [pipelineSession, pipelineStatus?.status]);

  const isPipelineMode = selectedTier === 'deep-analysis-baseball';

  const validateFile = useCallback((file) => {
    if (!ACCEPTED_VIDEO_TYPES.includes(file.type)) return 'Unsupported format';
    const limit = isPipelineMode ? MAX_FILE_SIZE_GCS : MAX_FILE_SIZE_LEGACY;
    if (file.size > limit) return isPipelineMode ? 'File exceeds 10GB' : 'File exceeds 100MB';
    return null;
  }, [isPipelineMode]);

  const addFiles = useCallback((newFiles) => {
    const validated = [];
    const errors = [];
    for (const f of newFiles) {
      if (files.length + validated.length >= MAX_FILES) break;
      const err = validateFile(f);
      if (!err) {
        validated.push(f);
      } else {
        errors.push(`${f.name}: ${err}`);
      }
    }
    if (errors.length > 0) {
      setFileErrors(errors);
      setTimeout(() => setFileErrors([]), 5000);
    }
    setFiles((prev) => [...prev, ...validated]);
  }, [files.length, validateFile]);

  const removeFile = useCallback((idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setFileStatuses((prev) => { const n = { ...prev }; delete n[idx]; return n; });
    setFileProgress((prev) => { const n = { ...prev }; delete n[idx]; return n; });
  }, []);

  const onDragOver = useCallback((e) => { e.preventDefault(); setIsDragging(true); }, []);
  const onDragLeave = useCallback(() => setIsDragging(false), []);
  const onDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(Array.from(e.dataTransfer.files));
  }, [addFiles]);

  const onFileSelect = useCallback((e) => {
    addFiles(Array.from(e.target.files));
    e.target.value = '';
  }, [addFiles]);

  // ── Upload a single file via GCS signed URL (with XHR for progress) ──
  const uploadFileToGCS = useCallback(async (file, idx, sessionId) => {
    // 1. Get signed upload URL
    const urlRes = await fetch('/api/unc/video/upload-url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fileName: file.name,
        contentType: file.type || 'video/mp4',
        sessionId,
      }),
    });
    if (!urlRes.ok) throw new Error('Failed to get upload URL');
    const urlData = await urlRes.json();
    const { uploadUrl, gcsUri, gcsPath } = urlData.data || urlData;

    // 2. Upload directly to GCS via signed URL (XHR for progress)
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl, true);
      xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const pct = Math.round((e.loaded / e.total) * 100);
          setFileProgress((prev) => ({ ...prev, [idx]: pct }));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Upload failed: ${xhr.status}`));
      };
      xhr.onerror = () => reject(new Error('Upload network error'));
      xhr.send(file);
    });

    return { gcsUri, gcsPath, fileName: file.name, contentType: file.type, fileSize: file.size };
  }, []);

  // ── Deep Analysis pipeline (GCS + bbclip) ──
  const handlePipelineAnalyze = useCallback(async () => {
    if (files.length === 0 || isAnalyzing) return;
    setIsAnalyzing(true);
    setPipelineSession(null);
    setPipelineStatus(null);

    try {
      // Upload all files to GCS
      const uploadedFiles = [];
      for (let i = 0; i < files.length; i++) {
        setFileStatuses((prev) => ({ ...prev, [i]: 'analyzing' }));
        setFileProgress((prev) => ({ ...prev, [i]: 0 }));
        try {
          const result = await uploadFileToGCS(files[i], i);
          uploadedFiles.push(result);
          setFileStatuses((prev) => ({ ...prev, [i]: 'complete' }));
        } catch (err) {
          console.error(`[UNCVideoHub] Upload failed for ${files[i].name}:`, err);
          setFileStatuses((prev) => ({ ...prev, [i]: 'error' }));
        }
      }

      if (uploadedFiles.length === 0) {
        setIsAnalyzing(false);
        return;
      }

      // Create session
      const sessionRes = await fetch('/api/unc/video/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          files: uploadedFiles,
          prompt: analysisPrompt,
          homeTeam: 'UNC',
          awayTeam: opponentTeam || 'Opponent',
          gameDate: gameDate || null,
          gameHalf,
        }),
      });
      const sessionData = await sessionRes.json();
      const session = sessionData.data || sessionData;
      setPipelineSession(session.sessionId);

      // Trigger analysis
      await fetch('/api/unc/video/trigger-analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: session.sessionId }),
      });

      setPipelineStatus({ status: 'analyzing', progress: 5, message: 'Pipeline triggered...' });
    } catch (err) {
      console.error('[UNCVideoHub] Pipeline error:', err);
      setPipelineStatus({ status: 'error', message: err.message });
    }

    setIsAnalyzing(false);
  }, [files, isAnalyzing, analysisPrompt, opponentTeam, gameDate, gameHalf, uploadFileToGCS]);

  // ── Pitch Tipping + Standard analysis (legacy paths) ──
  const handleAnalyze = useCallback(async () => {
    if (files.length === 0 || isAnalyzing) return;

    // Deep Analysis goes through pipeline
    if (isPipelineMode) {
      return handlePipelineAnalyze();
    }

    setIsAnalyzing(true);

    const isPitchTipping = selectedTier === 'pitch-tipping';

    if (isPitchTipping) {
      const firstFile = files[0];
      const blobUrl = URL.createObjectURL(firstFile);
      const playerName = playerAssoc === 'unc-roster'
        ? roster.find((p) => (p.id || p.playerId) === selectedPlayer)?.name || ''
        : '';

      setOverlayPhase('analyzing');
      setOverlayProgress(0);
      setOverlayVideoUrl(blobUrl);
      setOverlayMeta({ filename: firstFile.name, playerName, context });

      let expertIdx = 0;
      const expertInterval = setInterval(() => {
        setOverlayExpert(EXPERT_PERSONAS[expertIdx]);
        expertIdx = (expertIdx + 1) % EXPERT_PERSONAS.length;
      }, 2500);
      setOverlayExpert(EXPERT_PERSONAS[0]);

      const startTime = Date.now();
      const progressInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const pct = Math.min(95, (elapsed / 35000) * 100);
        setOverlayProgress(pct);
        if (pct >= 95) clearInterval(progressInterval);
      }, 300);

      try {
        const base64 = await fileToBase64(firstFile);
        const mediaItems = files.map((f, i) => ({
          id: `${Date.now()}-${i}`,
          name: f.name,
          file: f,
          type: MediaType.VIDEO,
          mimeType: f.type || 'video/mp4',
          base64Data: i === 0 ? base64 : null,
          size: f.size,
        }));
        if (files.length > 1) {
          for (let i = 1; i < mediaItems.length; i++) {
            mediaItems[i].base64Data = await fileToBase64(files[i]);
          }
        }

        const result = await analyzeMedia({
          mediaItems,
          analysisMode: 'pitch-tipping',
          model: 'pro',
        });

        clearInterval(progressInterval);
        clearInterval(expertInterval);
        setOverlayProgress(100);

        const analysisText = typeof result === 'string' ? result : result?.text || result?.content || JSON.stringify(result);
        setOverlayAnalysis(analysisText);
        setOverlayPhase('results');
      } catch (err) {
        clearInterval(progressInterval);
        clearInterval(expertInterval);
        console.error('[UNCVideoHub] Pitch tipping analysis failed:', err);
        setOverlayPhase(null);
        setFileStatuses({ 0: 'error' });
      }
      setIsAnalyzing(false);
      return;
    }

    // Standard analysis path (Quick Scan)
    const mode = ANALYSIS_MODES[selectedTier];
    const prompt = mode?.defaultPrompt || 'Analyze this video.';

    for (let i = 0; i < files.length; i++) {
      setFileStatuses((prev) => ({ ...prev, [i]: 'analyzing' }));
      setFileProgress((prev) => ({ ...prev, [i]: 0 }));

      const progressInterval = setInterval(() => {
        setFileProgress((prev) => {
          const cur = prev[i] || 0;
          if (cur >= 90) { clearInterval(progressInterval); return prev; }
          return { ...prev, [i]: cur + Math.random() * 15 };
        });
      }, 800);

      try {
        const base64 = await fileToBase64(files[i]);
        const mimeType = files[i].type || 'video/mp4';

        const apiRes = await fetch('/api/video-analysis', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            video: base64,
            mimeType,
            mode: selectedTier,
            prompt,
            playerContext: context === 'Opponent' ? 'opponent' : 'own',
            playerAssociation: playerAssoc === 'unc-roster' ? selectedPlayer : playerAssoc,
            filename: files[i].name,
          }),
        });

        clearInterval(progressInterval);

        if (apiRes.ok) {
          setFileProgress((prev) => ({ ...prev, [i]: 100 }));
          setFileStatuses((prev) => ({ ...prev, [i]: 'complete' }));
        } else {
          setFileStatuses((prev) => ({ ...prev, [i]: 'error' }));
        }
      } catch {
        clearInterval(progressInterval);
        setFileStatuses((prev) => ({ ...prev, [i]: 'error' }));
      }
    }

    setIsAnalyzing(false);
  }, [files, isAnalyzing, selectedTier, context, playerAssoc, selectedPlayer, roster, isPipelineMode, handlePipelineAnalyze]);

  // If pitch tipping overlay is active, render it
  if (overlayPhase) {
    return (
      <ScoutingIntelOverlay
        videoUrl={overlayVideoUrl}
        analysisText={overlayAnalysis}
        meta={overlayMeta}
        phase={overlayPhase}
        progress={overlayProgress}
        currentExpert={overlayExpert}
        onClose={() => {
          setOverlayPhase(null);
          setOverlayAnalysis(null);
          if (overlayVideoUrl) URL.revokeObjectURL(overlayVideoUrl);
          setOverlayVideoUrl(null);
          setOverlayMeta({});
        }}
      />
    );
  }

  return (
    <div className="space-y-5">
      {/* Drop zone */}
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        className="rounded-xl p-8 text-center cursor-pointer transition-all duration-200"
        style={{
          background: isDragging ? C.goldDim + '33' : C.s1,
          border: `2px dashed ${isDragging ? CAROLINA_BLUE : C.border}`,
        }}
      >
        <Film size={32} style={{ color: isDragging ? CAROLINA_BLUE : C.t3 }} className="mx-auto mb-3" />
        <p className="text-sm font-medium mb-1" style={{ color: C.text }}>
          Drop videos here or click to browse
        </p>
        <p className="text-xs" style={{ color: C.t3 }}>
          MP4, MOV, WebM — {isPipelineMode ? 'up to 10GB' : 'up to 100MB'} each, {MAX_FILES} max
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="video/mp4,video/quicktime,video/webm"
          multiple
          onChange={onFileSelect}
          className="hidden"
        />
      </div>

      {/* Analysis Tier */}
      <div>
        <label className="text-[10px] font-semibold tracking-widest uppercase mb-2 block" style={{ color: C.t3 }}>
          Analysis Level
        </label>
        <div className="flex gap-2">
          {ANALYSIS_TIERS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setSelectedTier(id)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all duration-200"
              style={{
                background: selectedTier === id ? CAROLINA_BLUE : C.s2,
                color: selectedTier === id ? '#000' : C.t2,
                border: `1px solid ${selectedTier === id ? CAROLINA_BLUE : C.border}`,
              }}
            >
              <Icon size={13} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Deep Analysis: baseball-specific controls */}
      {isPipelineMode && (
        <div className="space-y-4">
          {/* Analysis Prompt */}
          <div>
            <label className="text-[10px] font-semibold tracking-widest uppercase mb-2 block" style={{ color: C.t3 }}>
              <MessageSquare size={10} className="inline mr-1" />
              Analysis Focus (all 8 lenses will prioritize this)
            </label>
            <textarea
              value={analysisPrompt}
              onChange={(e) => setAnalysisPrompt(e.target.value.slice(0, 2000))}
              placeholder='e.g., "Focus on pitcher #32 changeup tells and fatigue in late innings" or "Analyze defensive positioning against left-handed batters"'
              rows={3}
              className="w-full px-3 py-2 rounded-lg text-xs outline-none resize-none"
              style={{ background: C.s2, border: `1px solid ${C.border}`, color: C.text }}
            />
            <p className="text-[10px] mt-1" style={{ color: C.t3 }}>
              {analysisPrompt.length}/2000 characters
            </p>
          </div>

          {/* Game context row */}
          <div className="flex gap-3 flex-wrap">
            {/* Opponent */}
            <div className="flex-1 min-w-[160px]">
              <label className="text-[10px] font-semibold tracking-widest uppercase mb-1.5 block" style={{ color: C.t3 }}>
                Opponent
              </label>
              <input
                type="text"
                value={opponentTeam}
                onChange={(e) => setOpponentTeam(e.target.value)}
                placeholder="e.g., Duke"
                className="w-full px-3 py-2 rounded-lg text-xs outline-none"
                style={{ background: C.s2, border: `1px solid ${C.border}`, color: C.text }}
              />
            </div>

            {/* Game Date */}
            <div className="min-w-[160px]">
              <label className="text-[10px] font-semibold tracking-widest uppercase mb-1.5 block" style={{ color: C.t3 }}>
                <Calendar size={10} className="inline mr-1" />
                Game Date
              </label>
              <input
                type="date"
                value={gameDate}
                onChange={(e) => setGameDate(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-xs outline-none"
                style={{ background: C.s2, border: `1px solid ${C.border}`, color: C.text }}
              />
            </div>

            {/* Game Half */}
            <div className="min-w-[160px]">
              <label className="text-[10px] font-semibold tracking-widest uppercase mb-1.5 block" style={{ color: C.t3 }}>
                Portion
              </label>
              <div className="flex gap-1.5">
                {['first', 'second', 'full'].map((h) => (
                  <button
                    key={h}
                    onClick={() => setGameHalf(h)}
                    className="px-3 py-2 rounded-lg text-xs font-medium transition-all"
                    style={{
                      background: gameHalf === h ? CAROLINA_BLUE : C.s2,
                      color: gameHalf === h ? '#000' : C.t2,
                      border: `1px solid ${gameHalf === h ? CAROLINA_BLUE : C.border}`,
                    }}
                  >
                    {h === 'first' ? '1st Half' : h === 'second' ? '2nd Half' : 'Full'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Player Association (non-pipeline modes) */}
      {!isPipelineMode && (
        <div className="flex gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] font-semibold tracking-widest uppercase mb-2 block" style={{ color: C.t3 }}>
              Player Association
            </label>
            <div className="relative">
              <button
                onClick={() => setShowPlayerDropdown(!showPlayerDropdown)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs"
                style={{ background: C.s2, border: `1px solid ${C.border}`, color: C.text }}
              >
                <span>
                  {playerAssoc === 'unc-roster'
                    ? (selectedPlayer
                      ? roster.find((p) => (p.id || p.playerId) === selectedPlayer)?.name || 'Select player...'
                      : 'UNC Roster — Select player...')
                    : playerAssoc === 'external' ? 'External Player' : 'Anonymous'}
                </span>
                <ChevronDown size={12} style={{ color: C.t3 }} />
              </button>
              {showPlayerDropdown && (
                <div
                  className="absolute z-20 mt-1 w-full rounded-lg overflow-hidden shadow-lg max-h-52 overflow-y-auto"
                  style={{ background: C.s2, border: `1px solid ${C.border}`, scrollbarWidth: 'thin' }}
                >
                  <button
                    onClick={() => { setPlayerAssoc('external'); setShowPlayerDropdown(false); }}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-800 transition-colors"
                    style={{ color: C.t2 }}
                  >
                    External Player
                  </button>
                  <button
                    onClick={() => { setPlayerAssoc('anonymous'); setShowPlayerDropdown(false); }}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-800 transition-colors"
                    style={{ color: C.t2 }}
                  >
                    Anonymous
                  </button>
                  {roster.length > 0 && (
                    <div className="border-t" style={{ borderColor: C.border }}>
                      <p className="px-3 py-1.5 text-[10px] uppercase tracking-widest" style={{ color: C.t3 }}>
                        UNC Roster
                      </p>
                      {roster.map((p) => {
                        const pid = p.id || p.playerId;
                        return (
                          <button
                            key={pid}
                            onClick={() => {
                              setPlayerAssoc('unc-roster');
                              setSelectedPlayer(pid);
                              setShowPlayerDropdown(false);
                            }}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-800 transition-colors"
                            style={{ color: C.text }}
                          >
                            {p.name}{p.position ? ` — ${p.position}` : ''}{p.number ? ` #${p.number}` : ''}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="text-[10px] font-semibold tracking-widest uppercase mb-2 block" style={{ color: C.t3 }}>
              Context
            </label>
            <div className="flex gap-2">
              {PLAYER_CONTEXTS.map((ctx) => (
                <button
                  key={ctx}
                  onClick={() => setContext(ctx)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all duration-200"
                  style={{
                    background: context === ctx ? C.s3 : C.s2,
                    color: context === ctx ? C.text : C.t2,
                    border: `1px solid ${context === ctx ? C.borderLight : C.border}`,
                  }}
                >
                  {ctx}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* File validation errors */}
      {fileErrors.length > 0 && (
        <div className="rounded-xl p-3 flex items-start gap-2" style={{ background: '#FF444411', border: '1px solid #FF444444' }}>
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" style={{ color: '#FF4444' }} />
          <div className="text-xs" style={{ color: '#FF4444' }}>
            {fileErrors.map((e, i) => <div key={i}>{e}</div>)}
          </div>
        </div>
      )}

      {/* Pipeline status banner */}
      {pipelineStatus && (
        <div
          className="rounded-xl p-4 flex items-center gap-3"
          style={{
            background: pipelineStatus.status === 'complete' ? '#00CC6611' :
                        pipelineStatus.status === 'error' ? '#FF444411' :
                        pipelineStatus.status === 'skipped' ? '#FFaa0011' : C.s1,
            border: `1px solid ${
              pipelineStatus.status === 'complete' ? '#00CC6644' :
              pipelineStatus.status === 'error' ? '#FF444444' :
              pipelineStatus.status === 'skipped' ? '#FFaa0044' : C.border
            }`,
          }}
        >
          {pipelineStatus.status === 'analyzing' && (
            <Loader2 size={16} className="animate-spin flex-shrink-0" style={{ color: CAROLINA_BLUE }} />
          )}
          {pipelineStatus.status === 'complete' && (
            <CheckCircle size={16} className="flex-shrink-0" style={{ color: '#00CC66' }} />
          )}
          {pipelineStatus.status === 'error' && (
            <AlertCircle size={16} className="flex-shrink-0" style={{ color: '#FF4444' }} />
          )}
          {pipelineStatus.status === 'skipped' && (
            <AlertCircle size={16} className="flex-shrink-0" style={{ color: '#FFaa00' }} />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium" style={{ color: C.text }}>
              {pipelineStatus.status === 'analyzing' ? 'Analyzing your video...' :
               pipelineStatus.status === 'complete' ? 'Analysis Complete' :
               pipelineStatus.status === 'skipped' ? 'Analysis Skipped' : 'Analysis Failed'}
            </p>
            <p className="text-[10px] truncate" style={{ color: C.t2 }}>
              {pipelineStatus.status === 'error'
                ? 'Analysis encountered an error. Please try again.'
                : (pipelineStatus.message || '')}
            </p>
          </div>
          {pipelineStatus.status === 'analyzing' && pipelineStatus.progress > 0 && (
            <span className="text-xs font-mono" style={{ color: CAROLINA_BLUE }}>
              {pipelineStatus.progress}%
            </span>
          )}
        </div>
      )}

      {/* Analyze button */}
      {files.length > 0 && (
        <button
          onClick={handleAnalyze}
          disabled={isAnalyzing}
          className="w-full py-3 rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-50"
          style={{ background: CAROLINA_BLUE, color: '#000' }}
        >
          {isAnalyzing ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 size={14} className="animate-spin" />
              {isPipelineMode ? 'Uploading & Analyzing...' : 'Analyzing...'}
            </span>
          ) : isPipelineMode ? (
            <span className="flex items-center justify-center gap-2">
              <Activity size={14} />
              Deep Analyze {files.length} Video{files.length !== 1 ? 's' : ''} (8 Lenses)
            </span>
          ) : (
            `Analyze ${files.length} Video${files.length !== 1 ? 's' : ''}`
          )}
        </button>
      )}

      {/* File grid */}
      {files.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {files.map((f, i) => (
            <FileCard
              key={`${f.name}-${i}`}
              file={f}
              onRemove={() => removeFile(i)}
              status={fileStatuses[i] || 'pending'}
              progress={fileProgress[i]}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PRACTICE FILM TAB
// ═══════════════════════════════════════════════════════════════════════════

function PracticeFilmTab() {
  const [roster, setRoster] = useState([]);
  const [files, setFiles] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [analysisState, setAnalysisState] = useState('idle'); // idle | uploading | analyzing | done | error
  const [analysisResult, setAnalysisResult] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    fetch('/api/unc/roster')
      .then((r) => r.ok ? r.json() : [])
      .then((data) => {
        const d = data?.data || data;
        setRoster(Array.isArray(d) ? d : d.roster || []);
      })
      .catch(() => setRoster([]));
  }, []);

  const addFiles = useCallback((newFiles) => {
    const validated = [];
    for (const f of newFiles) {
      if (files.length + validated.length >= MAX_FILES) break;
      if (ACCEPTED_VIDEO_TYPES.includes(f.type) && f.size <= MAX_FILE_SIZE_GCS) {
        validated.push(f);
      }
    }
    setFiles((prev) => [...prev, ...validated]);
  }, [files.length]);

  const onDragOver = useCallback((e) => { e.preventDefault(); setIsDragging(true); }, []);
  const onDragLeave = useCallback(() => setIsDragging(false), []);
  const onDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(Array.from(e.dataTransfer.files));
  }, [addFiles]);

  const runAnalysis = useCallback(async () => {
    if (files.length === 0) return;
    setAnalysisState('analyzing');
    try {
      // Create video URL for player
      const url = URL.createObjectURL(files[0]);
      setVideoUrl(url);
      // Analyze via Gemini vision
      const result = await analyzeMedia(files[0], MediaType.VIDEO, 'biomechanics');
      // Parse heatmap + path data from the analysis
      const parsed = parsePracticeAnalysis(result);
      setAnalysisResult(parsed);
      setAnalysisState('done');
    } catch (err) {
      console.error('[PracticeFilm] Analysis error:', err);
      setAnalysisState('error');
    }
  }, [files]);

  const resetAnalysis = useCallback(() => {
    setAnalysisState('idle');
    setAnalysisResult(null);
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setVideoUrl(null);
    setFiles([]);
  }, [videoUrl]);

  // If analysis is done, show results view
  if (analysisState === 'done' && analysisResult) {
    return (
      <div className="space-y-5">
        {/* Back button */}
        <button
          onClick={resetAnalysis}
          className="flex items-center gap-1.5 text-[10px] font-medium px-3 py-1.5 rounded-lg transition-colors hover:opacity-80"
          style={{ background: C.s2, border: `1px solid ${C.border}`, color: C.t2 }}
        >
          <RefreshCw size={10} /> New Analysis
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Video player */}
          <div className="rounded-xl overflow-hidden" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
            {videoUrl && (
              <video
                ref={videoRef}
                src={videoUrl}
                controls
                className="w-full"
                style={{ maxHeight: 400 }}
              />
            )}
            {selectedPlayer && (
              <div className="px-3 py-2 text-[10px] font-medium" style={{ color: C.t2, borderTop: `1px solid ${C.border}` }}>
                Linked to: {roster.find(p => (p.id || p.playerId) === selectedPlayer)?.name || selectedPlayer}
              </div>
            )}
          </div>

          {/* Diamond field heatmap + paths overlay */}
          <PracticeFilmOverlay
            heatmapData={analysisResult.heatmap || {}}
            pathLines={analysisResult.paths || []}
            title={files[0]?.name || 'Practice Film Analysis'}
          />
        </div>

        {/* Pitch Zone Heatmap (5×5 strike zone from DiamondVision data) */}
        {analysisResult.gameId && (
          <PracticeHeatmap gameId={analysisResult.gameId} />
        )}

        {/* Analysis summary */}
        {analysisResult.summary && (
          <div className="rounded-xl p-4" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
            <h4 className="text-xs font-semibold mb-2" style={{ color: C.text }}>Analysis Summary</h4>
            <p className="text-[11px] leading-relaxed whitespace-pre-wrap" style={{ color: C.t2 }}>
              {analysisResult.summary}
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Player selector */}
      <div>
        <label className="text-[10px] font-semibold tracking-widest uppercase mb-2 block" style={{ color: C.t3 }}>
          Link to Player
        </label>
        <select
          value={selectedPlayer}
          onChange={(e) => setSelectedPlayer(e.target.value)}
          className="w-full max-w-sm px-3 py-2 rounded-lg text-xs outline-none"
          style={{ background: C.s2, border: `1px solid ${C.border}`, color: C.text }}
        >
          <option value="">Select player...</option>
          {roster.map((p) => (
            <option key={p.id || p.playerId} value={p.id || p.playerId}>
              {p.name}{p.position ? ` — ${p.position}` : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        className="rounded-xl p-8 text-center cursor-pointer transition-all duration-200"
        style={{
          background: isDragging ? C.goldDim + '33' : C.s1,
          border: `2px dashed ${isDragging ? CAROLINA_BLUE : C.border}`,
        }}
      >
        <Clapperboard size={28} style={{ color: isDragging ? CAROLINA_BLUE : C.t3 }} className="mx-auto mb-3" />
        <p className="text-sm font-medium mb-1" style={{ color: C.text }}>
          Upload practice film
        </p>
        <p className="text-xs" style={{ color: C.t3 }}>
          MP4, MOV, WebM — up to 100MB each
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="video/mp4,video/quicktime,video/webm"
          multiple
          onChange={(e) => { addFiles(Array.from(e.target.files)); e.target.value = ''; }}
          className="hidden"
        />
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((f, i) => (
            <div
              key={`${f.name}-${i}`}
              className="flex items-center justify-between px-3 py-2 rounded-lg"
              style={{ background: C.s2, border: `1px solid ${C.border}` }}
            >
              <div className="flex items-center gap-2">
                <Film size={14} style={{ color: C.t3 }} />
                <span className="text-xs" style={{ color: C.text }}>{f.name}</span>
                <span className="text-[10px]" style={{ color: C.t3 }}>{formatBytes(f.size)}</span>
              </div>
              <button onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}>
                <Trash2 size={12} style={{ color: C.t3 }} className="hover:text-red-400 transition-colors" />
              </button>
            </div>
          ))}

          {/* Analyzing indicator */}
          {analysisState === 'analyzing' && (
            <div className="flex items-center justify-center gap-2 py-4">
              <Loader2 size={16} className="animate-spin" style={{ color: CAROLINA_BLUE }} />
              <span className="text-xs" style={{ color: C.t2 }}>Analyzing practice film with Gemini 3.1 Pro...</span>
            </div>
          )}

          {analysisState === 'error' && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: '#EF444422', border: '1px solid #EF444444' }}>
              <AlertCircle size={14} style={{ color: '#EF4444' }} />
              <span className="text-xs" style={{ color: '#EF4444' }}>Analysis failed. Try a shorter clip or different format.</span>
            </div>
          )}

          <button
            onClick={runAnalysis}
            disabled={analysisState === 'analyzing'}
            className="w-full py-2.5 rounded-xl text-xs font-semibold transition-all duration-200 disabled:opacity-50"
            style={{ background: CAROLINA_BLUE, color: '#000' }}
          >
            {analysisState === 'analyzing'
              ? 'Analyzing...'
              : `Analyze ${files.length} Practice Video${files.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      )}
    </div>
  );
}

// Parse Gemini analysis response into heatmap + path data
function parsePracticeAnalysis(result) {
  const text = typeof result === 'string' ? result : result?.text || result?.analysis || JSON.stringify(result);
  const parsed = { heatmap: {}, paths: [], summary: text };

  // Try to extract structured data from JSON in the response
  try {
    const jsonMatch = text.match(/```json\s*([\s\S]*?)```/) || text.match(/\{[\s\S]*"heatmap"[\s\S]*\}/);
    if (jsonMatch) {
      const data = JSON.parse(jsonMatch[1] || jsonMatch[0]);
      if (data.heatmap) parsed.heatmap = data.heatmap;
      if (data.paths) parsed.paths = data.paths;
      if (data.summary) parsed.summary = data.summary;
      return parsed;
    }
  } catch { /* fall through to heuristic parsing */ }

  // Heuristic: generate sample heatmap from text mentions of zones
  const zoneKeywords = {
    'Home Plate': /home\s*plate|batter|batting/i,
    'Pitcher\'s Mound': /mound|pitcher|pitching/i,
    '1st Base': /first\s*base|1st\s*base|1b/i,
    '2nd Base': /second\s*base|2nd\s*base|2b/i,
    '3rd Base': /third\s*base|3rd\s*base|3b/i,
    'SS Area': /short\s*stop|ss\s/i,
    'LF': /left\s*field|lf\s/i,
    'CF': /center\s*field|cf\s/i,
    'RF': /right\s*field|rf\s/i,
  };
  for (const [zone, regex] of Object.entries(zoneKeywords)) {
    const matches = (text.match(new RegExp(regex.source, 'gi')) || []).length;
    if (matches > 0) parsed.heatmap[zone] = matches;
  }

  return parsed;
}

// ═══════════════════════════════════════════════════════════════════════════
// GAME FILM HEATMAP TAB
// ═══════════════════════════════════════════════════════════════════════════

const KNOWN_GAMES = [
  { id: 'ORIOLES_VS_TIGERS_03.15.26', label: 'Orioles vs Tigers — Mar 15, 2026' },
  { id: 'YANKEES_VS_REDSOX_03.20.26', label: 'Yankees vs Red Sox — Mar 20, 2026' },
];

function GameFilmHeatmapTab() {
  const [selectedGameId, setSelectedGameId] = useState('');
  const [customGameId, setCustomGameId] = useState('');

  const gameId = selectedGameId || customGameId || '';

  return (
    <div className="space-y-5">
      {/* Game selector */}
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <label className="text-[10px] font-semibold tracking-widest uppercase mb-1.5 block" style={{ color: C.t3 }}>
            Select Game
          </label>
          <select
            value={selectedGameId}
            onChange={(e) => { setSelectedGameId(e.target.value); setCustomGameId(''); }}
            className="px-3 py-2 rounded-lg text-xs outline-none min-w-[260px]"
            style={{ background: C.s2, border: `1px solid ${C.border}`, color: C.text }}
          >
            <option value="">Choose analyzed game...</option>
            {KNOWN_GAMES.map(g => (
              <option key={g.id} value={g.id}>{g.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-semibold tracking-widest uppercase mb-1.5 block" style={{ color: C.t3 }}>
            Or Enter Game ID
          </label>
          <input
            type="text"
            value={customGameId}
            onChange={(e) => { setCustomGameId(e.target.value); setSelectedGameId(''); }}
            placeholder="e.g. TEAM_VS_TEAM_MM.DD.YY"
            className="px-3 py-2 rounded-lg text-xs outline-none min-w-[260px]"
            style={{ background: C.s2, border: `1px solid ${C.border}`, color: C.text }}
          />
        </div>
      </div>

      {/* Heatmap visualization */}
      <PracticeHeatmap gameId={gameId} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// LIBRARY TAB
// ═══════════════════════════════════════════════════════════════════════════

function LibraryTab() {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setLoading(true);
    fetch('/api/video-analysis')
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setAnalyses(Array.isArray(data) ? data : data.analyses || []))
      .catch(() => setAnalyses([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = analyses.filter((a) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (a.filename || '').toLowerCase().includes(q) ||
      (a.mode || '').toLowerCase().includes(q) ||
      (a.playerName || '').toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={20} className="animate-spin" style={{ color: C.t3 }} />
      </div>
    );
  }

  if (analyses.length === 0) {
    return (
      <EmptyState
        icon={Library}
        title="Your video library is empty"
        description="Analyzed videos will appear here. Upload and analyze your first video to get started."
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Search bar */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: C.t3 }} />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search analyses..."
          className="w-full pl-9 pr-3 py-2 rounded-lg text-xs outline-none"
          style={{ background: C.s2, border: `1px solid ${C.border}`, color: C.text }}
        />
      </div>

      {/* Analysis list */}
      <div className="space-y-2">
        {filtered.map((a, i) => {
          const modeConfig = ANALYSIS_MODES[a.mode];
          return (
            <div
              key={a.id || i}
              className="rounded-lg p-3 flex items-center justify-between hover:bg-zinc-900 transition-colors cursor-pointer"
              style={{ background: C.s1, border: `1px solid ${C.border}` }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded flex items-center justify-center"
                  style={{ background: C.s2 }}
                >
                  <Film size={16} style={{ color: modeConfig?.color || C.t3 }} />
                </div>
                <div>
                  <p className="text-xs font-medium" style={{ color: C.text }}>{a.filename || 'Untitled'}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: C.s3, color: C.t2 }}>
                      {modeConfig?.shortName || a.mode || 'Analysis'}
                    </span>
                    {a.playerName && (
                      <span className="text-[10px]" style={{ color: C.t3 }}>{a.playerName}</span>
                    )}
                    {a.createdAt && (
                      <span className="text-[10px]" style={{ color: C.t3 }}>
                        {new Date(a.createdAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <CheckCircle size={14} style={{ color: '#00CC66' }} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT: UNCVideoHub
// ═══════════════════════════════════════════════════════════════════════════

export default function UNCVideoHub() {
  const [activeTab, setActiveTab] = useState('upload');

  return (
    <div className="space-y-4">
      {/* Sub-tab bar */}
      <div className="flex gap-1 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
        {SUB_TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all duration-200 ${
              activeTab === id
                ? 'text-black'
                : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-900'
            }`}
            style={activeTab === id ? { background: CAROLINA_BLUE } : { background: C.s2 }}
          >
            <Icon size={13} />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'upload' && <UploadAnalyzeTab />}

      {activeTab === 'practice' && <PracticeFilmTab />}

      {activeTab === 'game-film' && <GameFilmHeatmapTab />}

      {activeTab === 'library' && <LibraryTab />}

      {activeTab === 'vision-tools' && <VisionModelCards />}

      {activeTab === 'live' && (
        <Suspense
          fallback={
            <div className="flex items-center justify-center py-20">
              <Loader2 size={20} className="animate-spin mr-2" style={{ color: C.t3 }} />
              <span className="text-xs" style={{ color: C.t3 }}>Loading Live Analysis...</span>
            </div>
          }
        >
          <LiveVideoTab />
        </Suspense>
      )}
    </div>
  );
}
